import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { setThumbnailsToUpdate } from './thumbnails';
import { savePage } from './pagesController';
import { saveConfigs, updateEditableSliderConfig } from './configs';
import { addToUndos } from './history';
import { Source } from '../reducer/history';
import { EditorType, VerticalAlignClassName } from '../constants';
import { IndesignTextFieldsConfig } from '../utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from '../utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from '../utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from '../utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from '../utils/editableLinks/getData';
import { ButtonConfig } from '../utils/indesignEditor/editableButtons/getData';
import { IframeConfig } from '../utils/indesignEditor/editableIframes/getData';
import { EnrichmentConfig } from '../utils/indesignEditor/editableEnrichments/getData';
import { SliderConfig, ImportingSlideData } from '../utils/indesignEditor/editableSliders/getData';
import { ThemedItemConfig } from '../utils/indesignEditor/themedItems/getData';
import { DuplicatedItemInSpreadConfig } from '../utils/indesignEditor/html';
import * as helpers from '../utils/helpers';
import { getAspDriveItem } from '../../DriveItemList/actions';
import {
	importFile,
	addFileInImport,
	FileError,
	ImportResponse,
} from '../../ImportManager/actions';
import { Api } from '../../../typings/api';

export enum EditableItemsActionKeys {
	SET_EDITABLE_ITEMS_DATA = '@@editor/editableItems/SET_EDITABLE_ITEMS_DATA',
	UPDATE_EDITABLE_TEXT_FIELD = '@@editor/editableItems/UPDATE_EDITABLE_TEXT_FIELD',
	UPDATE_EDITABLE_MEDIA = '@@editor/editableItems/UPDATE_EDITABLE_MEDIA',
	UPDATE_EDITABLE_LINK = '@@editor/editableItems/UPDATE_EDITABLE_LINK',
	UPDATE_EDITABLE_BUTTON = '@@editor/editableItems/UPDATE_EDITABLE_BUTTON',
	UPDATE_EDITABLE_IFRAME = '@@editor/editableItems/UPDATE_EDITABLE_IFRAME',
	UPDATE_EDITABLE_ENRICHMENT = '@@editor/editableItems/UPDATE_EDITABLE_ENRICHMENT',
	UPDATE_EDITABLE_SLIDER = '@@editor/editableItems/UPDATE_EDITABLE_SLIDER',
}

export interface EditableItemsData {
	editableTextFields: IndesignTextFieldsConfig[] | HtmlTextFieldsConfig[];
	editableMedia: IndesignMediaElementConfig[] | HtmlMediaElementConfig[];
	editableLinks: LinkConfig[];
	editableButtons?: ButtonConfig[];
	editableIframes?: IframeConfig[];
	editableEnrichments?: EnrichmentConfig[];
	editableSliders?: SliderConfig[];
	themedItems?: ThemedItemConfig[];
	duplicatedItemsInSpread?: DuplicatedItemInSpreadConfig[];
}

interface EditableTextFieldData {
	content?: string;
	isEmpty?: boolean;
	verticalAlignClassName?: VerticalAlignClassName | '';
}

export interface EditableMediaData {
	frameWidth?: number;
	frameHeight?: number;
	frameAspectRatio?: number;
	width?: number;
	height?: number;
	x?: number;
	y?: number;
	driveLink?: string;
	externalLink?: string;
	label?: string;
	isExternalLinkActive?: boolean;
	canDragAndResize?: boolean;
	isLoaded?: boolean;
	isEditedFromBackoffice?: boolean;
	isVisible?: boolean;
}

interface EditableLinkData {
	linkData: helpers.LinkData;
}

interface EditableButtonData {
	linkData?: helpers.LinkData;
	isVisible?: boolean;
}

interface EditableIframeData {
	src?: string;
	isVisible?: boolean;
}

interface EditableEnrichmentData {
	resource: Api.AspDriveResourceItemLight;
}

export interface EditableSliderData {
	isVisible?: boolean;
	deps?: string[];
	importingSlides?: ImportingSlideData[];
}

interface SetEditableItemsDataAction extends Action {
	type: EditableItemsActionKeys.SET_EDITABLE_ITEMS_DATA;
	page: number;
	data: EditableItemsData;
}

interface UpdateEditableTextFieldAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_TEXT_FIELD;
	page: number;
	id: string;
	data: EditableTextFieldData;
}

interface UpdateEditableMediaAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_MEDIA;
	page: number;
	id: string;
	data: EditableMediaData;
}

interface UpdateEditableLinkAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_LINK;
	page: number;
	id: string;
	data: EditableLinkData;
}

interface UpdateEditableButtonAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_BUTTON;
	page: number;
	id: string;
	data: EditableButtonData;
}

interface UpdateEditableIframeAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_IFRAME;
	page: number;
	id: string;
	data: EditableIframeData;
}

interface UpdateEditableEnrichmentAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_ENRICHMENT;
	page: number;
	id: string;
	data: EditableEnrichmentData;
}

interface UpdateEditableSliderAction extends Action {
	type: EditableItemsActionKeys.UPDATE_EDITABLE_SLIDER;
	page: number;
	id: string;
	data: EditableSliderData;
}

export type EditableItemsAction =
	SetEditableItemsDataAction |
	UpdateEditableTextFieldAction |
	UpdateEditableMediaAction |
	UpdateEditableLinkAction |
	UpdateEditableButtonAction |
	UpdateEditableIframeAction |
	UpdateEditableEnrichmentAction |
	UpdateEditableSliderAction;

export const setEditableItemsData = (page: number, data: EditableItemsData): SetEditableItemsDataAction => ({
	type: EditableItemsActionKeys.SET_EDITABLE_ITEMS_DATA,
	page,
	data,
});

export const updateEditableTextField = (
	page: number,
	id: string,
	data: EditableTextFieldData,
	shouldAddToUndos: boolean = true,
	isAccessiblePage: boolean = false,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableTextFields: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.TEXT_FIELD,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableTextFieldAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_TEXT_FIELD,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(setThumbnailsToUpdate([page]));

	const { editor: { editableTextFields } } = getState();
	const item = editableTextFields[page][id];
	const className = 'className' in item ? item.className : '';

	dispatch(savePage(page));

	if (!isAccessiblePage) {
		dispatch(saveAdjacentPageIfNeeded(className, page));
	}
};

export const updateEditableMedia = (
	page: number,
	id: string,
	data: EditableMediaData,
	shouldSave: boolean = true,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableMedia: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.MEDIA,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableMediaAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_MEDIA,
		page,
		id,
		data,
	};
	dispatch(action);

	if (shouldSave) {
		const {
			editor: {
				isAccessibleVersion,
			},
		} = getState();

		dispatch(setThumbnailsToUpdate([page]));

		const { editor: { editableMedia } } = getState();
		const item = editableMedia[page][id];
		const className = 'className' in item ? item.className : '';

		dispatch(savePage(page));

		if (!isAccessibleVersion) {
			dispatch(saveAdjacentPageIfNeeded(className, page));
		}
	}
};

export const updateEditableLink = (
	page: number,
	id: string,
	data: EditableLinkData,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableLinks: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.LINK,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableLinkAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_LINK,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(setThumbnailsToUpdate([page]));
	dispatch(savePage(page));
};

export const updateEditableButton = (
	page: number,
	id: string,
	data: EditableButtonData,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableButtons: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.BUTTON,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableButtonAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_BUTTON,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(setThumbnailsToUpdate([page]));

	const { editor: { editableButtons } } = getState();
	const { className } = editableButtons[page][id];

	dispatch(savePage(page));
	dispatch(saveAdjacentPageIfNeeded(className, page));
};

export const updateEditableIframe = (
	page: number,
	id: string,
	data: EditableIframeData,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableIframes: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.IFRAME,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableIframeAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_IFRAME,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(setThumbnailsToUpdate([page]));
	dispatch(savePage(page));
};

export const updateEditableEnrichment = (
	page: number,
	id: string,
	data: EditableEnrichmentData,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableEnrichments: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.ENRICHMENT,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableEnrichmentAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_ENRICHMENT,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(savePage(page));
};

export const updateEditableSlider = (
	page: number,
	id: string,
	data: EditableSliderData,
	shouldSave: boolean = true,
	shouldUpdateThumbnails?: boolean,
	shouldAddToUndos?: boolean,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				editableSliders: { [page]: { [id]: prevData } },
			},
		} = getState();

		dispatch(addToUndos({
			page,
			id,
			source: Source.SLIDER,
			data: {
				...prevData,
				isEditedFromBackoffice: true,
			},
		}));
	}

	const action: UpdateEditableSliderAction = {
		type: EditableItemsActionKeys.UPDATE_EDITABLE_SLIDER,
		page,
		id,
		data,
	};
	dispatch(action);

	if (shouldUpdateThumbnails) {
		dispatch(setThumbnailsToUpdate([page]));
	}

	if (shouldSave) {
		const { editor: { editableSliders } } = getState();
		const { className } = editableSliders[page][id];

		dispatch(savePage(page));
		dispatch(saveAdjacentPageIfNeeded(className, page));
	}
};

const saveAdjacentPageIfNeeded = (itemClassName: string, page: number) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			type,
			pagesController: { spreads, currentSpreadIndex },
			duplicatedItemsInSpread,
		},
	} = getState();

	if (type === EditorType.HTML) {
		return;
	}

	const currentSpread = spreads[currentSpreadIndex];
	const adjacentPage = currentSpread.find(pageInSpread => pageInSpread !== page);

	if (adjacentPage) {
		const duplicatedItemsInAdjacentPage = duplicatedItemsInSpread[adjacentPage];

		if (duplicatedItemsInAdjacentPage) {
			const hasDuplicatedItem = Object.values(duplicatedItemsInAdjacentPage).find(item => (
				item.className.includes(itemClassName)
			));

			if (hasDuplicatedItem) {
				dispatch(savePage(adjacentPage));
			}
		}
	}
};

export const importSlides = (
	page: number,
	sliderId: string,
	sliderIdInConfigs: string,
	newSlidesData: ImportingSlideData[],
	isFromDesktop?: boolean,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			editableSliders,
			configs: { pagesData: { [page - 1]: { sliders } } },
		},
	} = getState();
	const sliderData = sliders![sliderIdInConfigs];
	const prevSlides = 'slides' in sliderData ? sliderData.slides : sliderData;
	const { importingSlides: prevImportingSlides } = editableSliders[page][sliderId];
	const newImportingSlides = [...prevImportingSlides, ...newSlidesData];

	dispatch(updateEditableSlider(page, sliderId, {
		importingSlides: newImportingSlides,
	}, false));

	let importingSlides = newImportingSlides;

	while (importingSlides.length > 0) {
		const slideData = importingSlides[0];

		if (isFromDesktop) {
			const { id: slideId, file } = slideData;
			await dispatch(importFile({
				id: slideId,
				file: file as File,
				isOptimizedImageImport: true,
				isPublicationDependency: true,
				onImport: async ({ id }: Api.DriveItem) => {
					return await dispatch(addSlide(page, sliderId, sliderIdInConfigs, slideData, id));
				},
			}));
		} else {
			await dispatch(addSlide(page, sliderId, sliderIdInConfigs, slideData));
		}

		const {
			editor: { editableSliders },
			importManager: { byId },
		} = getState();
		const { importingSlides: currentImportingSlides } = editableSliders[page][sliderId];
		const getSlidesWithoutImportError = (({ id }: ImportingSlideData): boolean => (
			byId[id] ? !byId[id].error : true
		));
		importingSlides = currentImportingSlides.filter(getSlidesWithoutImportError);
	}

	dispatch(saveConfigs());
	dispatch(savePage(page));

	if (prevSlides.length === 0) {
		dispatch(setThumbnailsToUpdate([page]));
	}
};

const addSlide = (
	page: number,
	sliderId: string,
	sliderIdInConfigs: string,
	slideData: ImportingSlideData,
	resourceGlobalId?: number,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | ImportResponse> => {
	const { id: slideId, globalId } = slideData;
	const aspDriveItem = await dispatch(getAspDriveItem(resourceGlobalId || globalId as number));

	if (aspDriveItem) {
		const { type, label, relUrl, genericItemId, globalId, resolution, iconName } = aspDriveItem;
		const newSlideData = {
			...slideData,
			type,
			label,
			relUrl: helpers.getMediaUrl(relUrl),
			genericItemId,
			globalId,
			resolution,
			iconName,
		};
		const {
			editor: {
				configs: { pagesData: { [page - 1]: { sliders } } },
				editableSliders,
			},
		} = getState();
		const sliderData = sliders![sliderIdInConfigs];
		const slides = 'slides' in sliderData ? sliderData.slides : sliderData;
		const { importingSlides, deps } = editableSliders[page][sliderId];

		dispatch(updateEditableSlider(page, sliderId, {
			importingSlides: importingSlides.filter(({ id }) => id !== slideId),
			deps: [...deps, helpers.getDependencyString(newSlideData.globalId)],
		}, false));
		dispatch(updateEditableSliderConfig(page, sliderIdInConfigs, [...slides, newSlideData], false));
		return;
	} else {
		dispatch(addFileInImport(slideId, {
			error: FileError.IMPORT,
		}));

		return { isError: true };
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/editableItems.ts